import React from 'react';
import Head from 'next/head';
import {
    Config,
    Cookies,
    getCookie,
    setCookie,
    decode,
} from '@deporvillage/utils-library-component';
import {
    Layout,
    isInApp,
    NewRelic,
    GoogleTagManager,
    Runnea,
} from '@deporvillage/frontend-common';
import { CartContext } from '@deporvillage/frontend-common/src/context/cart/context';
import { GetCartItems } from '@deporvillage/frontend-common/src/context/cart/actions';

const Environment = {
    Production: 'prod',
};

// istanbul ignore next
const setLocationCurrency = (target, uenc) => {
    if (!target) return;
    target.addEventListener('click', (e) => {
        let url = e.target.getAttribute('data-location');
        if (!url) return null;
        if (uenc) url = `${decode(url)}uenc/${uenc}/`;
        else url = decode(url);
        return (window.location.href = url);
    });
};

const PageTemplate = ({ children, model: { layout } }) => {
    const {
        lang,
        store,
        geoIp,
        url,
        api,
        country,
        appleItunes,
        cdn: cdnConfig,
        environment,
        page,
        gtm,
        runnea,
        featureFlags,
    } = React.useContext(Config);

    const storeExt = store?.default_store;
    const geoExt = geoIp?.default_store;

    const [showGeoIP, setShowGeoIP] = React.useState(false);
    const [userData, setUserData] = React.useState({});
    const [cartData, dispatch] = React.useContext(CartContext);

    // istanbul ignore next
    const handleCountryListLoaded = ({ detail: { elem, uenc } = {} }) => {
        setLocationCurrency(elem, uenc);
    };

    // istanbul ignore next
    const handleUserData = (obj) => {
        window.removeEventListener('user:data', handleUserData);
        setUserData(obj.detail);
    };

    // istanbul ignore next
    const showGeoIpBanner = () => {
        if (!geoIp || !!getCookie(Cookies.VisitedCountry)) return;
        setCookie(Cookies.VisitedCountry, 1, 365);
        setShowGeoIP(true);
    };

    // istanbul ignore next
    const hideGeoIpBanner = () => {
        setShowGeoIP(false);
        /* eslint-disable-next-line no-undef */
        window.removeEventListener('geo-ip:close', hideGeoIpBanner);
    };

    /* istanbul ignore next */
    const getCart = async () => {
        if (!userData.session_id) return;
        const res = await fetch(`${api}/user/${lang}/cart?${Date.now()}`, {
            method: 'GET',
            headers: {
                sessionId: userData.session_id,
                country: country.code,
            },
            credentials: 'include',
        });
        const cartRes = await res.json();
        const cartData = {
            ...cartRes,
            isOpened: false,
        };
        dispatch(GetCartItems(cartData));
    };

    React.useEffect(() => {
        // @toDo FeatureFlag
        if (featureFlags?.enableNewAddToCart) {
            //
        } else {
            getCart();
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [userData]);

    React.useEffect(() => {
        isInApp();
        showGeoIpBanner();
        /* eslint-disable no-undef */
        window.dispatchEvent(new CustomEvent('user:getdata'));
        window.addEventListener('user:data', handleUserData);
        window.addEventListener('countries:loaded', handleCountryListLoaded);
        window.addEventListener('geo-ip:close', hideGeoIpBanner);
        return () => {
            window.removeEventListener('user:data', handleUserData);
            window.removeEventListener('countries:loaded', handleCountryListLoaded);
            window.removeEventListener('geo-ip:close', hideGeoIpBanner);
        };
        /* eslint-enable no-undef */
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    return (
        <>
            <Head>
                <meta
                    httpEquiv='X-UA-Compatible'
                    content='IE=edge,chrome=1'
                />

                <meta
                    name='viewport'
                    content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
                />

                <Layout.MetaIcons config={{ cdn: cdnConfig }} />

                {
                    appleItunes && appleItunes.enabled && (
                        <meta name='apple-itunes-app' content={appleItunes.appID} />
                    )
                }

                <link rel='preload' as='script' href='https://securepubads.g.doubleclick.net/tag/js/gpt.js' />

                <script
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{
                        __html: `
                            {
                                "@context": "http://schema.org",
                                "@type": "WebSite",
                                "url": "${url}",
                                "potentialAction": {
                                    "@type": "SearchAction",
                                    "target": "${url}/catalogsearch/result/?q={search_term_string}",
                                    "query-input": "required name=search_term_string"
                                }
                            }`,
                    }}
                />

            </Head>

            {
                <GoogleTagManager
                    enabled={gtm?.enabled}
                    containerId={gtm?.containerId}
                    dataLayerName={gtm?.dataLayerName}
                />
            }

            {
                <Runnea enabled={runnea?.enabled} />
            }

            {
                environment === Environment.Production && (
                    <NewRelic
                        page={page}
                        id={'1058728749'}
                        accountId={'707882'}
                        agentId={'1058728749'}
                        trustKey={'707882'}
                        license={'d21eccf0fd'}
                    />
                )
            }

            {
                layout && (
                    <div className='header'>
                        <Layout.Header
                            model={{ ...layout, ...cartData }}
                        />
                    </div>
                )
            }

            <div className={'main-container'}>
                {children}
            </div>

            {
                !userData.logged_in && (
                    <div className={'subscription'}>
                        <Layout.SubscriptionInlineForm />
                    </div>
                )
            }

            {
                layout && (
                    <div className={'footer'}>
                        <div className={'container container-full'}>
                            <Layout.Footer model={layout} />
                        </div>
                    </div>
                )
            }

            {
                /* istanbul ignore next */
                showGeoIP && (
                    <Layout.BannerGeoIp
                        extension={geoExt}
                        country={geoIp.code}
                        type={geoExt === storeExt ?
                            Layout.BannerGeoIpType.Delivery : Layout.BannerGeoIpType.Store}
                        url={url.replace(`.${storeExt}`, `.${geoExt}`)}
                    />
                )
            }
        </>
    );
};

export default PageTemplate;
